import { MutationTree } from 'vuex';
import moment from 'moment';
import { IDocumentState } from './document.store';
import { IDocument } from '@signing-page/documents/types/document';
import { EDocumentStatus } from '@signing-page/documents/types/enums/document-status';
import { IDocumentAttachment } from '@lib-shared/types/document-attachment';
import { ERecipientStatus } from '@signing-page/recipient/types/enums/recipient-status';
import { IRecipient } from '@signing-page/documents/types/recipient';
import { IDocumentRevision } from '@signing-page/documents/types/document-revision';

export const mutationSetLoading = 'setLoading';
export const mutationSetRevisionsLoading = 'setRevisionsLoading';
export const mutationSetRevisionsViewedLoading = 'setRevisionsViewedLoading';
export const mutationSetButtonLoading = 'setButtonLoading';

export const mutationSetForwarded = 'setForwarded';
export const mutationSetDocument = 'setDocument';
export const mutationSetDocumentRevisions = 'setDocumentRevisions';

export const mutationSetFocusedRequiredId = 'setFocusedRequiredId';
export const mutationSetDocumentStatus = 'setDocumentStatus';
export const mutationSetDocumentSignedDate = 'setDocumentSignedDate';

export const mutationSetRecipientStatus = 'setRecipientStatus';
export const mutationUpdateRecipients = 'updateRecipients';

export const mutationSetAttachmentView = 'setAttachmentView';
export const mutationSetDocumentAttachments = 'setDocumentAttachments';
export const mutationSetUploadingAttachment = 'setUploadingAttachment';
export const mutationRemoveUploadingAttachment = 'removeUploadingAttachment';
export const mutationSetError = 'setError';
export const mutationSetPdfUrl = 'setPdfUrl';

export const mutationSetIsDownloading = 'setIsDownloading';

export const mutations: MutationTree<IDocumentState> = {
  [mutationSetLoading](state: IDocumentState, loading: boolean) {
    state.isLoading = loading;
  },
  [mutationSetRevisionsLoading](state: IDocumentState, loading: boolean) {
    state.revisionsIsLoading = loading;
  },
  [mutationSetRevisionsViewedLoading](state: IDocumentState, loading: boolean) {
    state.revisionsViewedLoading = loading;
  },
  [mutationSetButtonLoading](state: IDocumentState, loading: boolean) {
    state.isButtonLoading = loading;
  },
  [mutationSetForwarded](state: IDocumentState, forwarded: boolean) {
    state.forwarded = forwarded;
  },
  [mutationSetDocument](state: IDocumentState, document: IDocument) {
    state.document = document;
  },
  [mutationSetDocumentRevisions](state: IDocumentState, documentRevisions: IDocumentRevision[]) {
    state.document.documentRevisions = documentRevisions;
  },
  [mutationSetPdfUrl](state: IDocumentState, pdfUrl: string) {
    state.pdfUrl = pdfUrl;
  },
  [mutationSetFocusedRequiredId](state: IDocumentState, focusedRequiredId: string) {
    state.focusedRequiredId = focusedRequiredId;
  },
  [mutationSetDocumentStatus](state: IDocumentState, status: EDocumentStatus) {
    state.document.status = status;
  },
  [mutationSetDocumentSignedDate](state: IDocumentState, date: string) {
    state.document.signDate = date;
  },
  [mutationUpdateRecipients](state: IDocumentState, recipients: IRecipient[]) {
    state.document.recipients = recipients;
  },
  [mutationSetRecipientStatus](
    state: IDocumentState,
    { recipientId, status }: { recipientId: string; status: ERecipientStatus }
  ) {
    const recipientsUpdated: IRecipient[] = state.document.recipients.map(recipient => {
      return recipient.id === recipientId ? { ...recipient, status } : recipient;
    });
    state.document = { ...state.document, recipients: recipientsUpdated };
  },
  [mutationSetDocumentAttachments](state: IDocumentState, attachments: IDocumentAttachment[]) {
    state.document.documentAttachments = attachments;
  },
  [mutationSetAttachmentView](state: IDocumentState, id: string) {
    const attachments: IDocumentAttachment[] = [...state.document.documentAttachments];
    const index: number = attachments.findIndex((item: IDocumentAttachment) => item.id === id);
    attachments[index].lastView = moment().toISOString();
    state.document.documentAttachments = attachments;
  },
  [mutationSetUploadingAttachment](state: IDocumentState, attachmentId: string) {
    state.uploadingAttachmentIds.push(attachmentId);
  },
  [mutationRemoveUploadingAttachment](state: IDocumentState, attachmentId: string) {
    const newIds: string[] = state.uploadingAttachmentIds.filter(
      (id: string) => id !== attachmentId
    );
    state.uploadingAttachmentIds = newIds;
  },
  [mutationSetError](state: IDocumentState, error: boolean) {
    state.error = error;
  },
  [mutationSetIsDownloading](state: IDocumentState, isDownloading: boolean) {
    state.isDownloading = isDownloading;
  },
};
